import { useTranslation } from "@msuite/ui/localization";
import { createRowActions, DataTable, type DataTableVariant, SubDataTable } from "@msuite/ui/table";
import { Tabs } from "@msuite/ui/tabs";
import type { GetArticlesQuerySchema } from "@msuite/utils/schemas";
import type React from "react";
import {
	articlesTableColumns,
	articlesTableLocationColumns,
	WarehouseLocationsMenu,
} from "@/components/stock";
import { useArticles } from "@/hooks/use-articles";
import { VendorArticles } from "@/sites/data/articles/vendor-articles";
import { WarehouseLocations } from "@/sites/data/warehouse-locations";
import { RowActions } from "./row-actions";

/** Props Interface */
interface ArticlesTableProps {
	menu?: React.ReactNode;
	variant?: DataTableVariant;
	query?: GetArticlesQuerySchema;
}

export function ArticlesTable({ menu = null, variant = "table", query }: ArticlesTableProps) {
	/** Hooks */
	const { articles, isLoading, queryKey, refetch } = useArticles(query);
	const { t } = useTranslation();

	const Item = variant === "sub-table" ? SubDataTable : DataTable;

	/** Computed */
	// Narrowed to one shelf, every row carries what stands on *that* shelf beside its
	// warehouse-wide balance. The server leaves that figure null for any wider list, so the column
	// only exists where it means something.
	const columns = query?.warehouseLocationId ? articlesTableLocationColumns : articlesTableColumns;

	/** Render */
	return (
		<Item
			data={articles}
			columns={[...columns, createRowActions(RowActions)]}
			isLoading={isLoading}
			refetch={refetch}
			queryKey={queryKey}
			menu={menu}
			persistenceKey="data-articles-table"
			initialPinningState={{
				start: [],
				end: [
					"stockBalance",
					// Absent from the column set unless the list names a shelf; an id nothing
					// matches is simply not pinned.
					"locationQuantity",
					"reservedQuantity",
					"availableQuantity",
					"warningStockBalance",
					"minimumStockBalance",
				],
			}}
			enableRowSelection
			enableMultiRowSelection
			initialShowGroupingHeader={false}
			getRowCanExpand={() => true}
			variant={variant}
			renderSubRows={(row) => {
				return (
					<Tabs
						variant="sub-table"
						className="stripes"
						withHeader={false}
						groups={[
							{
								name: "vendorArticles",
								panels: [
									{
										label: t("stock.vendors"),
										errors: [],
										panel: () => <VendorArticles articleId={row.original.id} />,
										cyId: "articles-tab",
									},
									{
										label: t("stock.warehouse_locations"),
										errors: [],
										panel: () => (
											<WarehouseLocations
												variant="sub-table"
												query={{ articleId: row.original.id }}
												menu={
													<WarehouseLocationsMenu
														query={{
															articleId: row.original.id,
														}}
													/>
												}
											/>
										),
										cyId: "storage-locations-tab",
									},
								],
							},
						]}
					/>
				);
			}}
		/>
	);
}
